import api from '@/api'
import dayjs from 'dayjs'
import { acceptHMRUpdate, defineStore } from 'pinia'
import mixpanel from 'mixpanel-browser'
import { AxiosError } from 'axios'
import { camelCase } from '@/utils/helperFunctions'

export const useMainStore = defineStore('main', {
  state: () => ({
    funnelStep: 0,
    // might move this
    showAutoEnrollInfoModal: false,
    courses: [] as CourseModel[],
    classes: [] as ClassModel[],
    student: {
      meta: [],
      autoFollowOn: null,
      studentfirstname: '',
      studentlastname: '',
      studentbirthday: '',
      gender: null,
      parent1firstname: null,
      parent1lastname: null,
      parent1email: '',
      parent1phone: '',
      timezone: '',
      school: '',
      acqSource: '',
      otherAcq: '',
      notes: '',
      // new
      studentGoals: [],
      whoseIdea: '',
      interestsMost: [],
      schoolOffersCoding: '',
      yearsCoding: '',
      codedBefore: '',
      codingTypesDone: [],
      languageExperience: [],
      funnelTerms: false,
      recommendedCourse: null,
      willingtospend: '',
      // main only
      mainTerms: false,
    } as CheckoutData,
    course: null as CourseModel,
    paid: false,
    total: 0, // deprecated on main route, only used in intro route
    jobId: '', // deprecated on main route, only used in intro route
    xtype: '', // deprecated on main route, only used in intro route

    smsSettings: {},

    prices: {},
    currentStep: 1,
    selectedTrack: {
      id: null,
      name: '',
      recommended: false,
      maxAge: 0,
      isCore: false,
      minAge: 0,
      description: '',
      course: {
        name: '',
        description: '',
        price: 0,
      },
    } as TrackModel,
    selectedClass: {
      id: null,
      name: '',
      classEnd: '',
    } as ClassModel,
    selectedCurrencyId: 1,
    currencies: [
      { id: 1, text: 'USD', value: 'usd', symbol: '$' },
      // { id: 2, text: 'GBP', value: 'gbp', symbol: '£' },
      // { id: 3, text: 'EUR', value: 'eur', symbol: '€' },
      // { id: 4, text: 'CAD', value: 'cad', symbol: '$' },
      // { id: 5, text: 'AED', value: 'aed', symbol: 'د.إ' },
    ],
    smsOpt: [] as string[],
    isPayingLumpSum: true,
    couponCodeInput: '',
    // paymentInfo: {
    //   amount: null,
    //   total: null,
    //   totalInCents: null,
    //   couponCode: null,
    //   discount: 0,
    //   offerCode: null,
    // },
    token: {},
    alert: {
      active: false,
      message: '',
      timeout: 3000,
      multiline: false,
      color: 'success',
    },
    /**Info needed in the confirmation page after checkout (main route) */
    userDetails: {
      username: '',
      password: '',
      studentId: 0,
      autoLoginUrl: '',
      jobId: null as number,
      xtype: 'regular',
      total: null as number,
    },
  }),
  getters: {
    studentAge(state) {
      if (!state.student.studentbirthday) return 1
      return dayjs().diff(state.student.studentbirthday, 'years')
    },
    studentTrack(state): string {
      if (!state.student.studentbirthday) return 'N/A'
      if (this.studentAge < 11) return 'elementary'
      else if (this.studentAge < 14) return 'middle'
      else return 'high'
    },
    currency(state) {
      if (!state.selectedCurrencyId) return null
      const curr = state.currencies.find(
        (curr) => curr.id === state.selectedCurrencyId
      )
      return curr
    },
  },
  actions: {
    async checkoutSuccess(nonce: string) {
      const response = await api.checkoutSuccess(nonce)
      this.userDetails.username = response.username
      this.userDetails.autoLoginUrl = response.autoLoginUrl
      this.userDetails.password = response.password
      this.userDetails.studentId = response.userId

      if (response.gtmEventFired) {
        console.log('conversion event ALREADY fired')
        return
      }
      await api.markGtmEventFired(nonce)
      this.sendEnrollmentGTM(response)
      localStorage.removeItem('MAIN_NONCE')
    },
    sendEnrollmentGTM(checkoutResponse: APICheckoutResponse) {
      console.log('conversion event fired')
      mixpanel.track('Enrollment Form Submit')
      gtag('config', 'G-R4JN5SDW5X', {
        user_id: checkoutResponse.userId,
        // debug_mode: true,
      })
      if (checkoutResponse.placementTest) {
        api.gtagEvent('api_signup')
      } else {
        api.gtagEvent('enrollment', {
          regClass: checkoutResponse.regClass,
          accClass: checkoutResponse.accClass,
          camp: checkoutResponse.camp,
          total: checkoutResponse.total,
        })
        api.gtagEvent('enrollmentFormSubmit', {
          email: checkoutResponse.email,
          event_id: String(checkoutResponse.jobId),
        })
        api.gtagEvent(
          `${camelCase(checkoutResponse.checkoutType)}PurchaseConfirmation`
        )
        this.sendPurchaseGtagFromLocalStorage()
      }
    },
    setCurrentStep(val: number) {
      if (val === 1) {
        api.mixpanel('Open Parent Information')
      } else if (val === 2) {
        api.mixpanel('Open Student Information')
      } else if (val === 3) {
        api.mixpanel('Open Choose Track')
      } else if (val === 4) {
        api.mixpanel('Open Schedule')
      } else if (val === 5) {
        api.mixpanel('Open Payment')
      }
      this.currentStep = val
    },
    async getPrice(lookupKey: string): Promise<number> {
      return new Promise<number>(async (resolve) => {
        if (this.prices[lookupKey]) {
          resolve(this.prices[lookupKey])
        }
        this.prices[lookupKey] = await api.getPrice(lookupKey)
        resolve(this.prices[lookupKey])
      })
    },
    submitPlacementCheckout() {
      return new Promise<APICheckoutResponse>((resolve, reject) => {
        const data = {
          student: this.student,
          smsOpt: this.smsOpt,
          classes: [],
          token: this.token,
          currency: this.currency.value,
          placementTest: true,
          checkoutType: 'placement',
        }
        let isSuccessful = false
        api
          .submitPlacementCheckout(data)
          .then((res) => {
            isSuccessful = true
            try {
              gtag('config', 'G-R4JN5SDW5X', { user_id: res.userId })
            } finally {
              resolve(res)
            }
          })
          .catch((err: AxiosError) => {
            console.error('Error in submitCheckout: ', err.toJSON())
            reject(err)
          })
      })
    },
    savePurchaseGtag(jobId: number) {
      const price = this.selectedTrack.course.price / 100
      const toSave = {
        transaction_id: jobId,
        value: price,
        currency: 'USD',
        items: [
          {
            item_id: this.selectedTrack.course.id,
            item_name: this.selectedClass.name,
            affiliation: 'Main Enrollment',
            item_brand: 'CodeWizardsHQ',
            item_category: `${this.selectedClass.channel}`,
            item_variant: `${this.selectedClass.frequency}`,
            price: price,
            quantity: 1,
          },
        ],
      }
      localStorage.setItem('gtag_on_purchase', JSON.stringify(toSave))
    },
    /** Sends the gtag purchase event that is saved in local storage, if one exists.
     * After sending the purchase event, the entry is removed from local storage.
     */
    sendPurchaseGtagFromLocalStorage() {
      const strOrNull = localStorage.getItem('gtag_on_purchase')
      if (strOrNull) {
        const toSend = JSON.parse(strOrNull)
        api.gtagEvent('purchase', toSend)
        localStorage.removeItem('gtag_on_purchase')
      }
    },
    createCheckoutSession(
      optionalParams: {
        placementTest?: boolean
        successUrl: string
      },
      classId: number | string = null,
      leadNonce: string = null,
      checkoutType: string = null
    ) {
      return new Promise<APICreateCheckoutResponse>((resolve, reject) => {
        let isSuccessful = false

        const cookie = (name) =>
          document.cookie
            .split('; ')
            .find((c) => c.startsWith(name + '='))
            ?.split('=')[1]

        const fbc = cookie('_fbc') ?? null
        const fbp = cookie('_fbp') ?? null

        let params: APICheckoutOptions = {
          student: this.student,
          smsOpt: this.smsOpt,
          classes: [],
          curriculumPathId: this.selectedTrack.id,
          startingSequence: this.selectedTrack.startingSequence,
          leadNonce,
          checkoutType,
          fbc,
          fbp,
        }

        if (classId) params.classes = [classId]
        else if (this.selectedClass.id) {
          params.classes = [this.selectedClass.id]
        }

        if (optionalParams) {
          params = { ...params, ...optionalParams }
        }

        api
          .submitCheckout(params)
          .then((res) => {
            isSuccessful = true
            try {
              // gtag('config', 'G-R4JN5SDW5X', { user_id: res.userId }) // user id doesn't exist yet
              this.savePurchaseGtag(res.jobId)
            } finally {
              resolve(res)
            }
          })
          .catch((err: AxiosError) => {
            console.error('Error in submitCheckout: ', err.toJSON())
            reject(err)
          })
      })
    },
  },
})

// HMR
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useMainStore, import.meta.hot))
}
